import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import { notify, notifyElement, ask } from '../../versa/msgbox.js';
import view from '../views/invoice-lots.js';
import form from '../forms/invoice-lots.js';
import * as types from '../types.js';

/** IDs das notas no lote: array JSON, string JSON (driver legado) ou texto agregado. */
const extractLotInvoiceIds = function(lot)
{
	let rawInv = lot.invoices;
	if (typeof rawInv === 'string' && rawInv.trim())
	{
		try
		{
			rawInv = JSON.parse(rawInv);
		}
		catch (e)
		{
			rawInv = null;
		}
	}
	if (Array.isArray(rawInv) && rawInv.length)
		return rawInv.map((x) => Number(x));
	const raw = lot.invoice_numbers;
	if (raw != null && String(raw).trim())
		return String(raw).trim().split(/\s+/).map(Number);
	return [];
};

const splitAligned = function(seq, n)
{
	if (seq == null || String(seq).trim() === '')
		return Array.from({ length: n }, () => '');
	const parts = String(seq).trim().split(/\s+/);
	return Array.from({ length: n }, (_, i) => parts[i] ?? '');
};

/** Mensagens agregadas no SQL com ` | ` entre notas. */
const splitInvoiceLastTextsAgg = function(s, n)
{
	if (s == null || String(s).trim() === '')
		return Array.from({ length: n }, () => '');
	const parts = String(s).split(/\s*\|\s*/).map(p => p.trim());
	return Array.from({ length: n }, (_, i) => parts[i] ?? '');
};

/** Corpo do detalhe montado em DOM (textContent): imune a `</…>` em dados da API. */
const buildLotInvoicesPanel = function(lot, invoiceIds, lotIdFromGrid)
{
	const root = document.createElement('div');
	root.className = 'lot-inv-detail';

	const meta = document.createElement('p');
	meta.className = 'lot-inv-detail__meta';
	const labelLot = lot.id != null ? lot.id : lotIdFromGrid;
	const b1 = document.createElement('strong');
	b1.textContent = 'Lote:';
	meta.appendChild(b1);
	meta.appendChild(document.createTextNode(' ' + String(labelLot) + ' \u00a0 '));
	const b2 = document.createElement('strong');
	b2.textContent = 'Notas:';
	meta.appendChild(b2);
	meta.appendChild(document.createTextNode(' ' + String(invoiceIds.length)));
	root.appendChild(meta);

	const head = document.createElement('div');
	head.className = 'lot-inv-detail__grid lot-inv-detail__grid--head';
	['NF', 'Origem', 'Chave', 'Código', 'Status', 'Mensagem'].forEach((lab) =>
	{
		const sp = document.createElement('span');
		sp.textContent = lab;
		head.appendChild(sp);
	});
	root.appendChild(head);

	const n = invoiceIds.length;
	const keys = splitAligned(lot.invoice_keys, n);
	const ioIds = splitAligned(lot.invoice_io_ids, n);
	const codes = splitAligned(lot.invoice_last_codes, n);
	const texts = splitInvoiceLastTextsAgg(lot.invoice_last_texts, n);

	for (let i = 0; i < n; i++)
	{
		const row = document.createElement('div');
		row.className = 'lot-inv-detail__grid lot-inv-detail__row';
		const code = codes[i] ?? '';
		const ok100 = String(code) === '100';
		const status = ok100 ? 'Autorizada' : (code ? 'Com retorno' : 'Pendente');
		const cells = [
			String(invoiceIds[i]),
			String(ioIds[i] ?? ''),
			String(keys[i] ?? ''),
			String(code),
			status,
			String(texts[i] ?? ''),
		];
		cells.forEach((txt) =>
		{
			const sp = document.createElement('span');
			sp.textContent = txt;
			row.appendChild(sp);
		});
		root.appendChild(row);
	}
	return root;
};

const invoiceLots = function()
{

	let invoice_lots_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_invoice_lots, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_invoice_lot, ...JSON.parse(id));
			_record['invoices'] = _record['invoices'].map(invoice => String(invoice)).join(' ');
			return _record;
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				type: data.type,
				invoices: data.invoices.split(' ').map(invoice => Number(invoice)),
			};
			if (old_data)
				return await api.call(api.update_invoice_lot, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_invoice_lot, data.company_id, _record);
		},
		sendLots()
		{
			if (!this.ensureSelection('Transmissão SEFAZ'))
				return;
			const sendRequest = async () =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				if (!ids.length)
				{
					notify('Selecione ao menos um lote.', 'Transmissão SEFAZ');
					return;
				}
				const pauseMs = 2500;
				let total = 0;
				let failed = 0;
				for (let i = 0; i < ids.length; i++)
				{
					const resp = await api.call(api.send_invoice_lots, { ids: [ids[i]] });
					if (resp == null)
					{
						failed++;
						break;
					}
					total += resp.count || 0;
					if (i + 1 < ids.length)
						await new Promise((r) => setTimeout(r, pauseMs));
				}
				const extra = failed ? ` (interrompeu após falha; ${failed} lote(s) não transmitido(s))` : '';
				notify(`Lotes transmitidos: ${total}${extra}`, 'Resultado');
				this.loadPage();
			};
			ask(`Transmitir ${this.getCount()} lote(s) para a Sefaz? (um por vez)`, 'Confirmação', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		queryLots()
		{
			if (!this.ensureSelection('Consulta SEFAZ'))
				return;
			const sendRequest = async () =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				if (!ids.length)
				{
					notify('Selecione ao menos um lote.', 'Consulta SEFAZ');
					return;
				}
				// Um lote por vez: evita estourar workers/SEFAZ e permite vários 103 pendentes.
				const pauseMs = 2500;
				let total = 0;
				let failed = 0;
				for (let i = 0; i < ids.length; i++)
				{
					const resp = await api.call(api.query_invoice_lots, { ids: [ids[i]] });
					if (resp == null)
					{
						failed++;
						break;
					}
					total += resp.count || 0;
					if (i + 1 < ids.length)
						await new Promise((r) => setTimeout(r, pauseMs));
				}
				const extra = failed ? ` (interrompeu após falha; ${failed} lote(s) não consultado(s))` : '';
				notify(`Lotes consultados: ${total}${extra}`, 'Resultado');
				this.loadPage();
			};
			ask(`Consultar ${this.getCount()} lote(s) com a Sefaz? (um por vez)`, 'Confirmação', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async showLotInvoices()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length)
			{
				notify('Selecione um lote de notas.', 'Notas do lote');
				return;
			}
			if (ids.length > 1)
			{
				notify('Selecione apenas um lote para detalhar.', 'Notas do lote');
				return;
			}
			const [companyId, lotId] = ids[0];
			const lot = await api.call(api.select_invoice_lot, companyId, lotId);
			if (!lot)
				return;
			const invoiceIds = extractLotInvoiceIds(lot);
			if (!invoiceIds.length)
			{
				notify('Nenhuma nota vinculada a este lote.', 'Notas do lote');
				return;
			}
			const panel = buildLotInvoicesPanel(lot, invoiceIds, lotId);
			notifyElement(panel, 'Notas do lote');
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do lote' },
		{ index: 15, field: 'invoice_count', label: 'Qtd. notas', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade de notas vinculadas ao lote' },
		{ index: 19, field: 'invoice_numbers', label: 'NFs', types: [], placeholder: '', title: 'Números das notas vinculadas ao lote' },
		{ index: 4, field: 'type', label: 'Tipo', types: [types.opt_invoice_lot_type(), types.datalist()], placeholder: '', title: 'Tipo de lote' },
		{ index: 6, field: 'receipt', label: 'Recibo', types: [types.nfe_receipt()], placeholder: '0', title: 'Número do recibo do lote' },
		{ index: 8, field: 'result_code', label: 'Código lote', types: [types.number(0, 2)], placeholder: '0', title: 'Código de retorno do lote' },
		{ index: 9, field: 'result_text', label: 'Mensagem lote', types: [], placeholder: '', title: 'Mensagem de retorno do lote' },
		{ index: 10, field: 'result_datetime', label: 'Data/Hora', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da última operação' },
		{ index: 5, field: 'state', label: 'Estado', types: [types.opt_uf, types.datalist()], placeholder: '', title: 'Unidade Federativa' },
		{ index: 3, field: 'environment', label: 'Ambiente', types: [types.opt_environment(), types.datalist()], placeholder: '', title: 'Ambiente de envio' },
		{ index: 12, field: 'in_contingency', label: 'Contingência', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Envio em contingência' },
		{ index: 20, field: 'invoice_keys', label: 'Chave NF', types: [], placeholder: '', title: 'Chave de acesso de uma nota do lote' },
		{ index: 21, field: 'invoice_io_ids', label: 'Entrada/Saída NF', types: [types.number(0, 2)], placeholder: '0', title: 'Entrada ou saída vinculada à nota' },
		{ index: 22, field: 'invoice_last_codes', label: 'Código NF', types: [types.number(0, 2)], placeholder: '0', title: 'Código de retorno das notas do lote' },
		{ index: 23, field: 'invoice_last_texts', label: 'Mensagem NF', types: [], placeholder: '', title: 'Mensagem de retorno das notas do lote' },
	];

	let operations = [
		{ hash: '#show-lot-invoices',	label: 'Ver&nbsp;notas&nbsp;do&nbsp;lote'	},
		{ hash: '#send-lots',	label: 'Transmitir&nbsp;lotes&nbsp;para&nbsp;Sefaz'	},
		{ hash: '#query-lots',	label: 'Consultar&nbsp;lotes&nbsp;pendentes'		}
	];

	router.set('#show-lot-invoices', invoice_lots_crud.showLotInvoices.bind(invoice_lots_crud));
	router.set('#send-lots', invoice_lots_crud.sendLots.bind(invoice_lots_crud));
	router.set('#query-lots', invoice_lots_crud.queryLots.bind(invoice_lots_crud));

	invoice_lots_crud.run(
		'invoice_lots',
		'Lotes&nbsp;Notas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations
	);

};

export default invoiceLots;

